/**
 * Criteria Store
 * 
 * Manages criteria definitions and session-scoped criteria selections.
 * 
 * SUPABASE-ONLY PERSISTENCE:
 * - Primary: Supabase (single source of truth)
 * - No localStorage cache (eliminates stale data issues)
 */

import { create } from 'zustand';
import type { Criteria } from '../../types/prioritization';
import {
  fetchAll,
  insertRecord,
  deleteRecord,
  isSupabaseAvailable,
  getCurrentWorkspaceId,
  createInitialSyncState,
  type SyncState,
} from '../../lib/supabaseSync';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import type { Database } from '../../types/database.generated';
import {
  criterionForAttribute,
  criterionReadingOf,
  type CriterionReading,
} from '../../lib/criterionReading';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';

const TABLE_NAME = 'criteria';

type CriteriaRow = Database['public']['Tables']['criteria']['Row'];

function rowToCriteria(row: CriteriaRow): Criteria {
  return {
    id: row.id,
    key: row.key,
    label: row.label,
    description: row.description ?? undefined,
    weight: row.weight ?? undefined,
    attributeKey: row.attribute_key ?? row.key,
    workspaceId: row.workspace_id,
    beneficiary: row.beneficiary,
    baseline: row.baseline,
    scale: row.scale,
  };
}

/** One save at a time per workspace criterion, so two quick edits never insert two rows. */
const readingSaves = new Map<string, Promise<unknown>>();

interface CriteriaState {
  criteria: Criteria[];
  selectedCriteriaIds: string[]; // Current selection
  framingStatement: string;
  
  // Sync state
  syncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: () => Promise<void>;
  
  // Actions
  addCriteria: (criteria: Criteria) => void;
  removeCriteria: (id: string) => void;
  /**
   * Say for whom a criterion measures, against what, and what its scale's ends
   * mean (migration 967). Writes the workspace's own criteria row for the
   * attribute, creating it the first time (carrying over what a shared row
   * already said). Only the parts passed are changed; blank clears a part.
   * The caller shows a failure where the person typed; nothing is toasted.
   */
  saveCriterionReading: (
    attribute: { key: string; label: string },
    parts: Partial<CriterionReading>,
  ) => Promise<{ error: string | null }>;
  selectCriteria: (id: string) => void;
  deselectCriteria: (id: string) => void;
  toggleCriteria: (id: string) => void;
  setFramingStatement: (statement: string) => void;
  
  // Getters
  getCriteriaById: (id: string) => Criteria | undefined;
  getSelectedCriteria: () => Criteria[];
}

export const useCriteriaStore = create<CriteriaState>()(
  (set, get) => ({
    criteria: [],
    selectedCriteriaIds: [],
    framingStatement: '',
      syncState: createInitialSyncState(),
      
      // ======================================================================
      // Supabase Sync
      // ======================================================================
      
      fetchFromSupabase: async () => {
        if (!isSupabaseAvailable()) {
          console.log('[CriteriaStore] Supabase not available');
          return;
        }
        
        set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
        
        const result = await fetchAll<Criteria>(TABLE_NAME, { column: 'id', ascending: true }, { filterByWorkspace: true });
        
        if (result.error) {
          set({ syncState: { ...get().syncState, isLoading: false, syncError: result.error } });
          console.error('[CriteriaStore] Failed to fetch from Supabase:', result.error);
          return;
        }
        
        if (result.data) {
          set({ 
            criteria: result.data,
            syncState: { ...get().syncState, isLoading: false, lastSyncAt: new Date(), syncError: null }
          });
          console.log(`[CriteriaStore] Loaded ${result.data.length} criteria from Supabase`);
        }
      },
      
      // ======================================================================
      // Actions
      // ======================================================================
      
      addCriteria: (criteria) => {
        // Update local state first (optimistic update)
        set((state) => ({
          criteria: [...state.criteria, criteria]
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          insertRecord<Criteria>(TABLE_NAME, criteria).then(result => {
            if (result.error) {
              console.error('[CriteriaStore] Failed to insert criteria to Supabase:', result.error);
              dispatchStoreError('Criteria', 'add criteria', result.error);
            }
          });
        }
      },
      
      removeCriteria: (id) => {
        set((state) => ({
          criteria: state.criteria.filter(c => c.id !== id),
          selectedCriteriaIds: state.selectedCriteriaIds.filter(cid => cid !== id)
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          deleteRecord(TABLE_NAME, id).then(result => {
            if (result.error) {
              console.error('[CriteriaStore] Failed to delete criteria from Supabase:', result.error);
              dispatchStoreError('Criteria', 'remove criteria', result.error);
            }
          });
        }
      },
      
      saveCriterionReading: (attribute, parts) => {
        const workspaceId = getCurrentWorkspaceId();
        if (!workspaceId) return Promise.resolve({ error: 'No workspace is selected.' });
        if (!isSupabaseAvailable()) return Promise.resolve({ error: 'Supabase not configured' });

        const patch: Partial<CriterionReading> = {};
        const cleaned = criterionReadingOf(parts);
        (Object.keys(parts) as (keyof CriterionReading)[]).forEach((f) => {
          patch[f] = cleaned[f];
        });

        const save = async (): Promise<{ error: string | null }> => {
          const db = getTypedSupabaseClient();
          const found = criterionForAttribute(get().criteria, attribute.key, workspaceId);
          const own = found && found.workspaceId === workspaceId ? found : undefined;

          if (own) {
            const before = own;
            set((state) => ({ criteria: state.criteria.map((c) => (c.id === own.id ? { ...c, ...patch } : c)) }));
            const { error } = await db
              .from(TABLE_NAME)
              .update({ ...patch, updated_at: new Date().toISOString() })
              .eq('id', own.id);
            if (error) {
              set((state) => ({ criteria: state.criteria.map((c) => (c.id === before.id ? before : c)) }));
              return { error: error.message };
            }
            return { error: null };
          }

          // The workspace's first word on this criterion: its own row, pointing at the attribute.
          const { data, error } = await db
            .from(TABLE_NAME)
            .insert({
              key: attribute.key,
              label: found?.label ?? attribute.label,
              description: found?.description ?? null,
              weight: found?.weight ?? null,
              attribute_key: attribute.key,
              workspace_id: workspaceId,
              ...criterionReadingOf(found),
              ...patch,
            })
            .select()
            .single();
          if (error || !data) {
            return { error: error?.message ?? 'The criterion was not saved.' };
          }
          set((state) => ({ criteria: [...state.criteria, rowToCriteria(data)] }));
          return { error: null };
        };

        const chainKey = `${workspaceId}::${attribute.key}`;
        const run = (readingSaves.get(chainKey) ?? Promise.resolve()).then(save);
        readingSaves.set(chainKey, run.catch(() => undefined));
        return run;
      },

      selectCriteria: (id) => {
        set((state) => ({
          selectedCriteriaIds: state.selectedCriteriaIds.includes(id)
            ? state.selectedCriteriaIds
            : [...state.selectedCriteriaIds, id]
        }));
      },
      
      deselectCriteria: (id) => {
        set((state) => ({
          selectedCriteriaIds: state.selectedCriteriaIds.filter(cid => cid !== id)
        }));
      },
      
      toggleCriteria: (id) => {
        set((state) => ({
          selectedCriteriaIds: state.selectedCriteriaIds.includes(id)
            ? state.selectedCriteriaIds.filter(cid => cid !== id)
            : [...state.selectedCriteriaIds, id]
        }));
      },
      
      setFramingStatement: (statement) => {
        set({ framingStatement: statement });
      },
      
      getCriteriaById: (id) => 
        get().criteria.find(c => c.id === id),
      
      getSelectedCriteria: () => {
      const { criteria, selectedCriteriaIds } = get();
      return criteria.filter(c => selectedCriteriaIds.includes(c.id));
    }
  })
);

